<script lang="ts" setup>
import * as Sentry from "@sentry/vue";
import { useProductPageStore } from "~/stores/main";

// -----------------------
// composables
// -----------------------
const router = useRouter();
const defaultStore = useProductPageStore();
const { getVariantByProperty, getAssetUrl } = useHelpers();
const config = useRuntimeConfig();

// -----------------------
// props & emits
// -----------------------
const props = withDefaults(
  defineProps<{
    sizeGuide?: boolean;
    disableUrlUpdates?: boolean;
    store?: any;
  }>(),
  {
    sizeGuide: false,
    disableUrlUpdates: false,
    store: null,
  },
);

// Use injected store or fallback to default
const store = props.store || defaultStore;

const emit = defineEmits(["show-size-guide", "select", "load"]);

// -----------------------
// computed properties
// -----------------------

/*
fixme: remove this as not used anymore?

const filteredVariantsRef = computed(() => {
  let products = store.masterProduct.products;

  if (store.color) {
    products = products.filter(
      (data: Record<any, any>) =>
        data.product.product_szin_property === store.color,
    );
  }

  if (store.size) {
    products = products.filter(
      (data: Record<any, any>) =>
        data.product.product_meret_property === store.size,
    );
  }

  return products;
});
*/

const { data: sizeTaxa } = await useAsyncData("sizeTaxa", async () => {
  if (!store.masterProduct?.products) return [];

  return await $fetch(`/api/meilisearch/taxon`, {
    method: "POST",
    body: {
      name: store.masterProduct.products
        .map((data: Record<any, any>) => data.product.product_meret_property)
        .filter(
          (size: string, i: number, self: string[]) => self.indexOf(size) === i,
        ),
      sort: "priority:asc",
      taxonomyId: parseInt(config.public.meilisearchSizeTaxonomyId),
    },
  }).catch((error) => {
    Sentry.captureEvent({
      message: `Failed to fetch taxon`,
      level: "error",
      extra: {
        key: "ER-KL9B",
        error,
      },
    });

    return [];
  });
});

const allSizes = computed(() => {
  if (sizeTaxa.value && Array.isArray(sizeTaxa.value)) {
    return sizeTaxa.value.map((taxa: any) => ({
      name: taxa.name,
      priority: taxa.priority,
    }));
  }

  return [];
});

const selectedVariantRef = computed(() => {
  if (!store.size || !store.color) {
    return null;
  }

  return store.masterProduct?.products.find((data: Record<any, any>) => {
    return (
      data.product.product_szin_property === store.color &&
      data.product.product_meret_property === store.size
    );
  });
});

const colorsRef = computed(() => {
  const colors = store.masterProduct?.products
    .filter((product) => product.product.stock > 0) // only include products with stock
    .map(
      (data: Record<any, any>) =>
        `${data.product.product_szin_property}|${data.product.product_paletta_property}`,
    ) // Collect all colors
    .filter(
      (color: string, i: number, self: string[]) => self.indexOf(color) === i,
    ); // Make the list uniquw
  /*
     Leave the "original" order to match ProductListProductCard.
     If we want alphabetical rendering back, need to do it for QickView and ProductListProductCard too
    */
  // .sort();

  if (!store.size) {
    return colors.map((color: string) => ({
      name: color.split("|")[0],
      value: color.split("|")[1],
      selectable: true,
    }));
  }

  const colorsWithSelectedSize = store.masterProduct.products
    .filter((data: Record<any, any>) => {
      return (
        data.product.product_meret_property === store.size &&
        data.product.stock > 0
      );
    })
    .map(
      (data: Record<any, any>) =>
        `${data.product.product_szin_property}|${data.product.product_paletta_property}`,
    );

  return colors.map((color: string) => ({
    name: color.split("|")[0],
    value: color.split("|")[1],
    selectable: colorsWithSelectedSize.includes(color),
  }));
});

const sizesRef = computed(() => {
  if (allSizes.value.length === 0) {
    return [];
  }

  const sizes = store.masterProduct.products
    .filter((product) => product.product.stock > 0) // only include products with stock
    .map((data: Record<any, any>) => data.product.product_meret_property) // Collect all sizes
    .filter(
      (size: string, i: number, self: string[]) => self.indexOf(size) === i,
    ) // Make the list unique
    .sort((a, b) => {
      const priorityA = allSizes.value.find((s) => s.name === a)?.priority;
      const priorityB = allSizes.value.find((s) => s.name === b)?.priority;

      return priorityA - priorityB;
    });

  if (!store.color) {
    return sizes.map((size: string) => ({ value: size, selectable: true }));
  }

  const sizesWithSelectedColor = store.masterProduct.products
    .filter((data: Record<any, any>) => {
      return (
        data.product.product_szin_property === store.color &&
        data.product.stock > 0
      );
    })
    .map((data: Record<any, any>) => data.product.product_meret_property);

  return sizes.map((size: string) => ({
    value: size,
    selectable: sizesWithSelectedColor.includes(size),
  }));
});

// -----------------------
// helper functions
// -----------------------
const selectColor = (color: string) => {
  store.color = color;
  const variant = getVariantByProperty(
    store.masterProduct,
    "product_szin_property",
    color,
  );

  if (
    typeof variant === "object" &&
    "images" in variant &&
    Array.isArray(variant.images)
  ) {
    store.images = getImages(variant);
  } else {
    store.images = [];
  }
};

const selectSize = (size: string) => {
  store.size = size;
};

const getImages = (variant) => {
  return variant.images.map((src) => getAssetUrl(src));
};

const updateQueryParameters = () => {
  const query = {
    color: encodeURIComponent(store.color),
  };

  if (store.size) {
    query.size = encodeURIComponent(store.size);
  }

  router.replace({ query });
};

// -----------------------
// vue events
// -----------------------
watch(selectedVariantRef, (newVariant) => {
  // console.log(newVariant?.product?.stock);

  if (newVariant?.product?.stock === 0) {
    store.size = null;
    store.selectedVariant = null;
    store.selectedVariantSku = null;
  } else {
    store.selectedVariant = newVariant ? newVariant.product.id : null;
    store.selectedVariantSku = newVariant ? newVariant.product.sku : null;

    if (!newVariant) {
      store.size = null;
    }
    emit("select", store.masterProduct, selectedVariantRef.value);
  }

  // console.log(store.selectedVariantSku);
});

// Only update URL parameters if not disabled
if (!props.disableUrlUpdates) {
  watch(store, updateQueryParameters);
}

onMounted(() => {
  if (store.color && store.masterProduct) {
    const variantWithColor = getVariantByProperty(
      store.masterProduct,
      "product_szin_property",
      store.color,
    );

    if (variantWithColor) {
      store.images = getImages(variantWithColor);
    }
  }

  if (selectedVariantRef.value) {
    store.selectedVariant = selectedVariantRef.value.product.id;
    store.selectedVariantSku = selectedVariantRef.value.product.sku;
  }

  emit("load", store.masterProduct, selectedVariantRef.value);
});
</script>

<template>
  <div>
    <hr class="border-black my-4.5 xl:mb-5" />
    <ProductPageColors
      :colors="colorsRef"
      :selected="store.color"
      :store="store"
      @change="selectColor"
    ></ProductPageColors>
    <template v-if="sizesRef && (store.masterProduct?.stock ?? 0 > 0)">
      <hr class="border-black my-4.5 xl:mt-3.5 xl:mb-5" />
      <ProductPageSizes
        class="mt-[10px]"
        :sizes="sizesRef"
        :selected="store.size"
        :size-guide="sizeGuide"
        @change="selectSize"
        @show-size-guide="$emit('show-size-guide')"
      ></ProductPageSizes>
    </template>
    <hr class="border-black my-4.5 xl:mt-3.5" />
  </div>
</template>
